import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { SystemActivity } from "../../models/home";

export type HomeNavigationTarget = "dates" | "activity-history";

export interface HomeNavigationIntent {
  target: HomeNavigationTarget;
  filter?: { role?: string };
}

@Injectable({ providedIn: "root" })
export class HomeInteractionStateService {
  private readonly selectedRoleSubject = new BehaviorSubject<string | null>(null);
  readonly selectedRole$: Observable<string | null> = this.selectedRoleSubject.asObservable();

  private readonly datesPanelSubject = new BehaviorSubject<boolean>(false);
  readonly datesPanelOpen$ = this.datesPanelSubject.asObservable();

  private readonly activityExpandedSubject = new BehaviorSubject<boolean>(false);
  readonly activityExpanded$ = this.activityExpandedSubject.asObservable();

  private readonly historyPanelSubject = new BehaviorSubject<boolean>(false);
  readonly historyPanelOpen$ = this.historyPanelSubject.asObservable();

  private readonly navigationIntentSubject = new Subject<HomeNavigationIntent>();
  readonly navigationIntent$ = this.navigationIntentSubject.asObservable();

  private readonly activityDetailSubject = new BehaviorSubject<SystemActivity | null>(null);
  readonly activityDetail$ = this.activityDetailSubject.asObservable();

  get activityDetailSnapshot(): SystemActivity | null {
    return this.activityDetailSubject.value;
  }

  get datesPanelOpenSnapshot(): boolean {
    return this.datesPanelSubject.value;
  }

  get historyPanelOpenSnapshot(): boolean {
    return this.historyPanelSubject.value;
  }

  get activityExpandedSnapshot(): boolean {
    return this.activityExpandedSubject.value;
  }

  get selectedRoleSnapshot(): string | null {
    return this.selectedRoleSubject.value;
  }

  selectRole(role: string | null): void {
    this.selectedRoleSubject.next(role);
  }

  toggleDatesPanel(open?: boolean): void {
    const nextState = open ?? !this.datesPanelSubject.value;
    this.datesPanelSubject.next(nextState);
    if (!nextState) {
      this.selectedRoleSubject.next(null);
    } else {
      this.historyPanelSubject.next(false);
      this.setActivityDetail(null);
    }
  }

  setActivityExpanded(expanded: boolean): void {
    this.activityExpandedSubject.next(expanded);
  }

  toggleHistoryPanel(open?: boolean): void {
    const nextState = open ?? !this.historyPanelSubject.value;
    this.historyPanelSubject.next(nextState);
    if (nextState) {
      this.datesPanelSubject.next(false);
      this.setActivityDetail(null);
    }
  }

  setActivityDetail(activity: SystemActivity | null): void {
    this.activityDetailSubject.next(activity);
    this.setActivityExpanded(Boolean(activity));
    if (activity) {
      this.datesPanelSubject.next(false);
      this.historyPanelSubject.next(false);
    }
  }

  requestNavigation(intent: HomeNavigationIntent): void {
    this.navigationIntentSubject.next(intent);
    if (intent.target === "dates") {
      if (intent.filter?.role) {
        this.selectRole(intent.filter.role);
      }
      this.toggleDatesPanel(true);
      return;
    }
    if (intent.target === "activity-history") {
      this.toggleHistoryPanel(true);
    }
  }
}
